import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  ReactNode,
} from 'react';
import { Link } from '@tanstack/react-router';

import {
  useHoldZone,
  useInteractionHold,
  type AttentionConcentration,
  type WorkspaceTaskSummary,
} from '@/features/tasks';
import { useCanWork } from '@/features/workspaces/canWork';
// From its own module, not the package's index: the index pulls in the Tasks
// page, whose modules import `@/features/tasks`, which imports this package
// back (through `fileChangeLink`). Read only at render; see below.
import { NEEDS_PERSON_STATES } from '@/features/workspace-tasks/listView';
import { SECTION_HEADING } from '@/shared/components/sectionHeadingClass';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { formatDurationWords } from '@/shared/utils/time';
import { GROUP_FIGURE, PANEL } from '../stateStyles';
import { useSteadyRows } from '../useSteadyRows';
import {
  queueWords,
  yourTurn,
  type QueueGroup,
  type QueueRow as Row,
  type YourTurn,
} from '../yourTurn';
import { BatchAction } from './BatchAction';
import { ConcentrationCallout } from './ConcentrationCallout';
import { HeldActionsProvider } from './HeldActionsProvider';
import { ReviewInARow } from './ReviewInARow';
import { KeysHelp } from './KeysHelp';
import { useHeldActions } from '../heldActionsContext';
import { prefersReducedMotion } from '../reducedMotion';
import { choiceLabel, firstChoice, reworkChoice } from '../rowChoices';
import { QueueRow } from './QueueRow';
import { SubtaskCrumb } from './SubtaskCrumb';

/**
 * Your turn (spec 152): everything in the workspace that waits for a person,
 * in one queue, each task once. It replaces the Needs attention and Waiting
 * on you groups.
 *
 * - The header says how many, why, and how long the oldest has waited.
 * - A parent's subtasks sit under it, the header leading to its run page.
 * - One action is primary at a time: the first action of the row the person
 *   is on (focus, else pointer, else the first row).
 * - The rows hold still while the pointer or focus is inside (spec 148), so
 *   nothing moves under a decision.
 * - After an accepted action focus moves to the next row, or to this
 *   heading when none is left (spec 151 FR-005).
 * - To a reader (`useCanWork()` false) it is "Needs attention": the same rows,
 *   waiting on the workspace's members, with no Review in a row and no keys
 *   that act (spec 193). The rows offer only what reading allows.
 */

const ZONE = 'overview-your-turn';
const HEADING_ID = 'overview-your-turn';

/**
 * The Tasks page's "Needs a person" view: the same tasks, every assignee.
 * Read when the link renders, never while modules load: this package and the
 * Tasks page's sit on an import cycle through `@/features/tasks`, and at load
 * time the list may not exist yet.
 */
function needsAPersonStates(): string {
  return NEEDS_PERSON_STATES.join(',');
}

export interface YourTurnSectionProps {
  workspaceId: string;
  attention: WorkspaceTaskSummary['attention'];
  concentration: AttentionConcentration | null;
}

interface Entry {
  group: QueueGroup;
  row: Row;
}

export function YourTurnSection({
  workspaceId,
  attention,
  concentration,
}: YourTurnSectionProps) {
  const turn = useMemo(() => yourTurn(attention), [attention]);
  const inQueue = useMemo(
    () =>
      new Set(
        turn.groups.flatMap((group) => group.rows.map((row) => row.taskId)),
      ),
    [turn],
  );
  // Every action taken in the queue, or the review sheet, is held here
  // first (spec 154 US2).
  return (
    <HeldActionsProvider workspaceId={workspaceId} present={inQueue}>
      <YourTurnBody
        workspaceId={workspaceId}
        turn={turn}
        concentration={concentration}
      />
    </HeldActionsProvider>
  );
}

function YourTurnBody({
  workspaceId,
  turn,
  concentration,
}: {
  workspaceId: string;
  turn: YourTurn;
  concentration: AttentionConcentration | null;
}) {
  const canWork = useCanWork();
  const words = queueWords(canWork);
  const holdZone = useHoldZone(ZONE);
  const holding = useInteractionHold(
    (state) =>
      state.menusOpen > 0 ||
      state.pointerZone === ZONE ||
      state.focusZone === ZONE,
  );
  const flat = useMemo<Entry[]>(
    () =>
      turn.shown.flatMap((group) => group.rows.map((row) => ({ group, row }))),
    [turn],
  );
  const idOf = useCallback((entry: Entry) => entry.row.taskId, []);
  const held = useSteadyRows(flat, idOf, holding);
  // The hold keeps other people's changes still, not the person's own: a row
  // they acted on leaves as soon as it has gone (spec 148 lets a person's own
  // move through the same way). A refused action rolls back, so that row is
  // still there and stays.
  const [actedOn, setActedOn] = useState<ReadonlySet<string>>(() => new Set());
  const present = new Set(flat.map((entry) => entry.row.taskId));
  const steady = held.filter(
    (entry) =>
      !(actedOn.has(entry.row.taskId) && !present.has(entry.row.taskId)),
  );
  const groups = regroup(steady);

  const [focusId, setFocusId] = useState<string | null>(null);
  const [pointerId, setPointerId] = useState<string | null>(null);
  const currentId = focusId ?? pointerId ?? steady[0]?.row.taskId ?? null;

  const handOff = useRef<HTMLElement | null>(null);
  // Where focus goes once the row acted on is decided: the next row's first
  // control, noted before the row changes under it.
  const prepareHandOff = (item: HTMLElement, list: HTMLElement) => {
    const acted = item.dataset.queueTask;
    if (acted && !actedOn.has(acted)) {
      setActedOn((previous) => new Set(previous).add(acted));
    }
    const items = [
      ...list.querySelectorAll<HTMLElement>('li[data-queue-task]'),
    ];
    const next = items
      .slice(items.indexOf(item) + 1)
      .map((other) => other.querySelector<HTMLElement>('a, button'))
      .find((control) => control !== null);
    handOff.current = next ?? null;
  };
  const noteHandOff = (event: MouseEvent<HTMLElement>) => {
    const item = rowOf(event.target);
    if (item) prepareHandOff(item, event.currentTarget);
  };
  const moveFocusOn = useCallback(() => {
    const next = handOff.current;
    if (next?.isConnected) {
      next.focus();
      return;
    }
    const heading = document.getElementById(HEADING_ID);
    if (heading) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }, []);

  const { hold } = useHeldActions();
  const rowsById = new Map(
    steady.map((entry) => [entry.row.taskId, entry.row]),
  );
  /*
    Keys, only while focus is in the queue and not in a field (spec 154 US5,
    WCAG 2.1.4): J and K move between rows, Enter opens a row's peek when the
    row itself has focus, A takes its first action, R its rework. Each is held
    like a click.
  */
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.defaultPrevented || event.repeat) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement;
    if (target.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }
    const item = rowOf(target);
    if (!item) return;
    const list = event.currentTarget;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;

    if (key === 'j' || key === 'k') {
      const items = [
        ...list.querySelectorAll<HTMLElement>('li[data-queue-task]'),
      ];
      const to = items[items.indexOf(item) + (key === 'j' ? 1 : -1)];
      event.preventDefault();
      to?.focus();
      return;
    }
    if (key === 'Enter') {
      if (target !== item) return;
      event.preventDefault();
      item.querySelector<HTMLElement>('[data-peek-toggle]')?.click();
      return;
    }
    if (key !== 'a' && key !== 'r') return;
    const row = rowsById.get(item.dataset.queueTask ?? '');
    if (!row) return;
    const choice =
      key === 'a' ? firstChoice(row, canWork) : reworkChoice(row, canWork);
    if (!choice) return;
    event.preventDefault();
    prepareHandOff(item, list);
    const held = hold({
      choice,
      rows: [row],
      label: choiceLabel(choice, row),
      subject: row.title,
    });
    if (held) moveFocusOn();
  };

  const phone = usePhoneWidth();
  const empty = steady.length === 0;
  // Whether the queue had rows while this page was open: emptying it then is
  // the person's doing, and the all-clear draws in (spec 155 FR-012).
  const [hadRows, setHadRows] = useState(!empty);
  if (!empty && !hadRows) setHadRows(true);
  const emptiedHere = empty && hadRows;
  const decisions = useMemo(
    () =>
      turn.groups.flatMap((group) =>
        group.rows.filter((row) => row.severity === 'decision'),
      ),
    [turn],
  );

  return (
    <section aria-labelledby={HEADING_ID} className="flex flex-col gap-2.5">
      <h2 id={HEADING_ID} className={SECTION_HEADING}>
        {words.heading}
      </h2>

      {empty ? (
        <AllClear celebrate={emptiedHere} title={words.allClear} />
      ) : (
        <>
          <ConcentrationCallout
            workspaceId={workspaceId}
            concentration={concentration}
          />
          <div className={PANEL} {...holdZone}>
            <Header
              total={turn.total}
              countWords={words.count(turn.total)}
              breakdown={turn.breakdown}
              oldestEnteredAt={turn.oldestEnteredAt}
              tools={
                // A phone has no keys to explain, so it gets no "?" (spec 268).
                phone && !(canWork && decisions.length > 0) ? null : (
                  <>
                    {/* A reader can decide none of them (spec 193). */}
                    {canWork && decisions.length > 0 ? (
                      <ReviewInARow
                        workspaceId={workspaceId}
                        decisions={decisions}
                      />
                    ) : null}
                    {phone ? null : (
                      <KeysHelp canWork={canWork} sectionName={words.heading} />
                    )}
                  </>
                )
              }
            />
            <div
              onClickCapture={noteHandOff}
              onKeyDown={onKeyDown}
              onFocus={(event: FocusEvent<HTMLElement>) => {
                setFocusId(rowOf(event.target)?.dataset.queueTask ?? null);
              }}
              onBlur={(event: FocusEvent<HTMLElement>) => {
                if (event.currentTarget.contains(event.relatedTarget)) return;
                setFocusId(null);
              }}
              onPointerOver={(event: PointerEvent<HTMLElement>) => {
                const id = rowOf(event.target)?.dataset.queueTask;
                if (id) setPointerId(id);
              }}
              onPointerLeave={() => {
                setPointerId(null);
              }}
            >
              {groups.map(({ group, rows }) => (
                <div
                  key={group.key}
                  data-queue-group
                  className="border-border border-t first:border-t-0"
                >
                  {group.parent ? (
                    <div className="flex items-center gap-2 px-3.5 pt-2.5 pb-1">
                      <p className="text-muted-foreground min-w-0 text-xs">
                        <SubtaskCrumb
                          workspaceId={workspaceId}
                          parent={group.parent}
                        />
                      </p>
                      <BatchAction
                        group={{ ...group, rows }}
                        onHeld={moveFocusOn}
                      />
                    </div>
                  ) : null}
                  <ul>
                    {rows.map((row) => (
                      <QueueRow
                        key={row.taskId}
                        workspaceId={workspaceId}
                        row={row}
                        current={row.taskId === currentId}
                        grouped={group.parent !== null}
                        onDone={moveFocusOn}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {turn.overflow > 0 ? (
              <Link
                to="/workspaces/$workspaceId/tasks"
                params={{ workspaceId }}
                search={{ state: needsAPersonStates() }}
                className="text-muted-foreground hover:text-foreground border-border block border-t px-3.5 py-2 text-xs underline-offset-4 hover:underline"
              >
                Show all {String(turn.total)} in Tasks
              </Link>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * "5 things need you · 1 needs attention · 4 decisions · oldest 2 days"; to a
 * reader, "5 things wait on its members · …".
 */
function Header({
  total,
  countWords,
  breakdown,
  oldestEnteredAt,
  tools,
}: {
  total: number;
  /** The words after the figure: "things need you" (`queueWords`). */
  countWords: string;
  breakdown: { wrong: number; decisions: number; toStart: number };
  oldestEnteredAt: string | null;
  /** "Review in a row" and the keys list (spec 154). */
  tools?: ReactNode;
}) {
  const parts = [
    breakdown.wrong > 0
      ? `${String(breakdown.wrong)} ${breakdown.wrong === 1 ? 'needs' : 'need'} attention`
      : null,
    breakdown.decisions > 0
      ? `${String(breakdown.decisions)} decision${breakdown.decisions === 1 ? '' : 's'}`
      : null,
    breakdown.toStart > 0 ? `${String(breakdown.toStart)} to start` : null,
  ].filter((part): part is string => part !== null);
  const oldest = formatDurationWords(oldestEnteredAt);
  if (oldest) parts.push(`oldest ${oldest}`);

  return (
    // The tools wrap under the count on a phone rather than squeezing the
    // words into a column beside it.
    <div className="border-border flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-3.5 py-3">
      <span
        aria-hidden="true"
        className={`${GROUP_FIGURE} text-foreground tabular-nums`}
      >
        {total}
      </span>
      <div className="flex min-w-[12rem] flex-1 flex-col">
        {/*
          The figure beside it is the number, so the words do not repeat it
          (spec 153 FR-017): "5 · 5 things need you" read the count twice.
          Assistive technology, which skips the figure, gets the sentence.
        */}
        <p className="text-foreground text-sm font-medium">
          <span aria-hidden="true">{countWords}</span>
          <span className="sr-only">
            {String(total)} {countWords}
          </span>
        </p>
        <p className="text-muted-foreground text-xs">{parts.join(' · ')}</p>
      </div>
      {tools ? (
        // On a phone the tools take their own full-width row under the
        // count, rather than a right-aligned scrap of one (spec 268).
        <div className="ms-auto flex shrink-0 items-center gap-1.5 max-md:ms-0 max-md:w-full max-md:[&>*]:flex-1">
          {tools}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The queue empty: the page's best state, said as one line (FR-010).
 *
 * When the person empties it themselves (`celebrate`), the ring and the check
 * draw in once (spec 155 FR-012). Opened already empty, it is drawn still.
 */
function AllClear({
  celebrate,
  title,
}: {
  celebrate: boolean;
  /** "Nothing waits for you", or a reader's words (`queueWords`). */
  title: string;
}) {
  const [drawn, setDrawn] = useState(
    () => !celebrate || prefersReducedMotion(),
  );
  useEffect(() => {
    if (drawn) return;
    const frame = requestAnimationFrame(() => {
      setDrawn(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [drawn]);
  const stroke = `motion-safe:transition-[stroke-dashoffset] motion-safe:duration-slow motion-safe:ease-standard`;
  return (
    <div
      data-celebrate={celebrate ? 'true' : undefined}
      className="border-border text-muted-foreground flex items-center gap-2.5 rounded-lg border border-dashed px-3.5 py-3 text-sm"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="text-board-state-done size-5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          pathLength={1}
          strokeDasharray="1"
          strokeDashoffset={drawn ? 0 : 1}
          className={stroke}
        />
        <path
          d="m9 12 2 2 4-4"
          pathLength={1}
          strokeDasharray="1"
          strokeDashoffset={drawn ? 0 : 1}
          className={`${stroke} motion-safe:delay-150`}
        />
      </svg>
      <div>
        <p className="text-foreground font-medium">{title}</p>
        <p className="text-xs">New decisions arrive here as they come.</p>
      </div>
    </div>
  );
}

/** The queue row an event happened in, if any. */
function rowOf(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element
    ? target.closest<HTMLElement>('li[data-queue-task]')
    : null;
}

/**
 * The held rows back into their groups, in the order they stand. A row keeps
 * the group it was shown in while the queue holds.
 */
function regroup(
  entries: readonly Entry[],
): { group: QueueGroup; rows: Row[] }[] {
  const out: { group: QueueGroup; rows: Row[] }[] = [];
  for (const entry of entries) {
    const last = out.at(-1);
    if (last && last.group.key === entry.group.key) last.rows.push(entry.row);
    else out.push({ group: entry.group, rows: [entry.row] });
  }
  return out;
}
